// Agents Kanban → "Scheduled" tab (WS-I).
//
// The board's "Board | Scheduled" toggle swaps the kanban for this view. It
// reads the WS-E aggregation (`api.agent.listScheduled`) — one flat row per
// scheduled unit of work, unioning named-agent schedule DECLARATIONS and
// enabled workflow schedules — and renders it as a kit DataTable.
//
// `active` is surfaced honestly (CLAUDE.md soft rule + WS-E contract note):
//   - active   → green "Scheduled" pill (a cron is actually registered/firing)
//   - !active  → muted "Not scheduled" pill + tooltip explaining it is declared
//                but not currently firing.
//
// Workflow rows deep-link into the workflows editor under the current view
// prefix (`/<surface>/workflows?id=<workflowId>`); `useWorkflowId` honours the
// `?id=` seed so the editor opens that workflow on mount. Agent rows are
// non-clickable (v1).

import type { ScheduledRun, ListScheduledRunsResponse } from '@septima/contract';
import { useQuery } from '@tanstack/react-query';
import { Workflow as WorkflowIcon, Bot } from 'lucide-react';
import { api } from '@/lib/api';
import { useRouter, usePathname } from '@/lib/navigation';
import { DataTable, Pill, EmptyState, type DataTableColumn } from '@/components/ui';
import { cn } from '@/lib/utils';

// ─── Helpers (exported for unit tests) ──────────────────────────────────────

/**
 * Humanise a 5-field cron expression for the common cases this app schedules
 * (daily / weekly / monthly at a fixed time). Falls back to the raw cron — the
 * caller renders the return value in `font-mono`, which reads correctly for the
 * raw-cron fallback too.
 */
export function humaniseCron(cron: string): string {
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return cron;
    const [min, hour, dom, month, dow] = parts;

    const isNum = (v: string) => /^\d+$/.test(v);
    const time =
        isNum(min) && isNum(hour) ? `${hour.padStart(2, '0')}:${min.padStart(2, '0')}` : null;
    if (!time) return cron;

    const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Daily: every day-of-month, every month, every day-of-week.
    if (dom === '*' && month === '*' && dow === '*') {
        return `Daily at ${time}`;
    }
    // Weekly: a fixed day-of-week.
    if (dom === '*' && month === '*' && isNum(dow)) {
        const day = DAYS[Number(dow) % 7];
        return `Weekly on ${day} at ${time}`;
    }
    // Monthly: a fixed day-of-month, every month.
    if (isNum(dom) && month === '*' && dow === '*') {
        const d = Number(dom);
        const ord =
            d % 10 === 1 && d !== 11
                ? 'st'
                : d % 10 === 2 && d !== 12
                  ? 'nd'
                  : d % 10 === 3 && d !== 13
                    ? 'rd'
                    : 'th';
        return `Monthly on the ${d}${ord} at ${time}`;
    }
    return cron;
}

/** en-GB date-time, or "—" when the timestamp is null/unparseable. */
export function formatDateTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const SURFACE_LABEL: Record<ScheduledRun['surface'], string> = {
    desk: 'Desk',
    ops: 'Ops',
    comply: 'Comply',
    view: 'View',
};

// ─── Presentational table (pure — unit-tested in isolation) ─────────────────

export function ScheduledRunsTable({
    scheduled,
    loading,
    onWorkflowClick,
}: {
    scheduled: ScheduledRun[];
    loading?: boolean;
    onWorkflowClick?: (run: ScheduledRun) => void;
}) {
    const columns: DataTableColumn<ScheduledRun>[] = [
        {
            id: 'name',
            header: 'Name',
            sortable: true,
            sortValue: (r) => r.name,
            cell: (r) => (
                <span className="font-medium text-[rgb(var(--color-text-primary))]">{r.name}</span>
            ),
        },
        {
            id: 'kind',
            header: 'Type',
            sortable: true,
            sortValue: (r) => r.kind,
            cell: (r) => (
                <span className="inline-flex items-center gap-1.5 text-annotation text-[rgb(var(--color-text-secondary))]">
                    {r.kind === 'workflow' ? (
                        <WorkflowIcon className="w-3.5 h-3.5" aria-hidden="true" />
                    ) : (
                        <Bot className="w-3.5 h-3.5" aria-hidden="true" />
                    )}
                    <Pill
                        variant={r.kind === 'workflow' ? 'blue' : 'grey'}
                        size="sm"
                        label={r.kind === 'workflow' ? 'Workflow' : 'Agent'}
                    />
                </span>
            ),
        },
        {
            id: 'schedule',
            header: 'Schedule',
            cell: (r) => (
                <span className="font-mono tabular-nums text-[rgb(var(--color-text-secondary))]">
                    {humaniseCron(r.cron)}
                </span>
            ),
        },
        {
            id: 'nextRun',
            header: 'Next Run',
            numeric: true,
            sortable: true,
            sortValue: (r) => r.nextRunAt ?? '',
            cell: (r) => (
                <span className="font-mono tabular-nums text-[rgb(var(--color-text-secondary))]">
                    {formatDateTime(r.nextRunAt)}
                </span>
            ),
        },
        {
            id: 'lastRun',
            header: 'Last Run',
            numeric: true,
            sortable: true,
            sortValue: (r) => r.lastRunAt ?? '',
            cell: (r) => (
                <span className="font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                    {formatDateTime(r.lastRunAt)}
                </span>
            ),
        },
        {
            id: 'surface',
            header: 'Surface',
            cell: (r) => <Pill variant="grey" size="sm" label={SURFACE_LABEL[r.surface]} />,
        },
        {
            id: 'status',
            header: 'Status',
            sortable: true,
            sortValue: (r) => (r.active ? 1 : 0),
            cell: (r) =>
                r.active ? (
                    <Pill variant="green" size="sm" label="Scheduled" />
                ) : (
                    <span title="Declared but not currently firing">
                        <Pill variant="grey" size="sm" label="Not scheduled" />
                    </span>
                ),
        },
    ];

    return (
        <DataTable
            ariaLabel="Scheduled agent and workflow runs"
            columns={columns}
            data={scheduled}
            rowKey={(r) => `${r.kind}:${r.ref ?? r.name}`}
            loading={loading}
            initialSort={{ id: 'nextRun', desc: false }}
            onRowClick={
                onWorkflowClick
                    ? (r) => {
                          if (r.kind === 'workflow') onWorkflowClick(r);
                      }
                    : undefined
            }
            empty={<EmptyState title="No scheduled runs" />}
        />
    );
}

// ─── Container (query + navigation) ─────────────────────────────────────────

/** Map the current pathname (`/ops/agents`, `/view/agents`, …) to the view
 *  prefix so the workflow deep-link stays inside the staff member's view. */
function viewPrefixFromPath(pathname: string): string {
    const seg = pathname.split('/').filter(Boolean)[0];
    return seg === 'desk' || seg === 'ops' || seg === 'comply' || seg === 'view' ? seg : 'ops';
}

export default function ScheduledRunsView() {
    const router = useRouter();
    const pathname = usePathname();

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['agent', 'scheduled'],
        queryFn: () => api.agent.listScheduled().then(unwrap<ListScheduledRunsResponse>),
    });

    if (isError) {
        return (
            <div className="p-6">
                <EmptyState
                    title="Could not load scheduled runs"
                    body={error instanceof Error ? error.message : 'Please try again.'}
                />
            </div>
        );
    }

    const scheduled = data?.scheduled ?? [];

    const handleWorkflowClick = (run: ScheduledRun) => {
        if (!run.ref) return;
        const prefix = viewPrefixFromPath(pathname);
        router.push(`/${prefix}/workflows?id=${encodeURIComponent(run.ref)}`);
    };

    return (
        <div className={cn('flex-1 overflow-auto p-6')}>
            <ScheduledRunsTable
                scheduled={scheduled}
                loading={isLoading}
                onWorkflowClick={handleWorkflowClick}
            />
        </div>
    );
}

// ─── ts-rest unwrap ─────────────────────────────────────────────────────────

/** Unwrap a typed ts-rest `{ status, body }` 200 envelope, throwing the body's
 *  `error` string on any non-200 so React Query surfaces it via `isError`. */
function unwrap<T>(res: { status: number; body: unknown }): T {
    if (res.status === 200) return res.body as T;
    const body = res.body as { error?: unknown } | undefined;
    const msg =
        body && typeof body.error === 'string'
            ? body.error
            : `Request failed (status ${res.status})`;
    throw new Error(msg);
}
